// productsettings.prices.role_adjust_prices repeats an identical rule
// definition (roleid/label/weight/txntype/adjustment/...) on every product
// that carries a given rbdid - only `amount` genuinely varies per product
// (confirmed against real catalog data, zero exceptions). This factors the
// definition out into a shared, ad-hoc-cached lookup keyed by rbdid, and
// leaves each product with a slim {rbdid, amount} reference. Consumers
// dereference via products/getResolvedRoleAdjustPrices or
// products/getResolvedTotalPriceAdjustment (see store/products.js) - never
// read productsettings.prices.role_adjust_prices directly downstream of
// this, since the npm price-adjustment code (@aspedia/vuejs-ecommerce-ui)
// isn't editable and expects fully-populated rule objects.
//
// Runs unconditionally (SSR and client both) - unlike the SSR-deferred
// fields in applyFieldMapper.js, price must be correct at first paint on
// every catalogue card, so this can't be a lazy-load/defer strategy.
export default function normalizeRoleAdjustPrices(store, product) {
  const rules = product && product.productsettings && product.productsettings.prices
    ? product.productsettings.prices.role_adjust_prices
    : undefined

  if (!Array.isArray(rules) || rules.length === 0) {
    return product
  }

  const existingDefinitions = store.getters.getAdHocStorageValue('roleAdjustRuleDefinitions') || {}
  const mergedDefinitions = { ...existingDefinitions }
  let definitionsChanged = false

  const slimRules = rules.map(rule => {
    const { rbdid, amount, ...definition } = rule
    if (!Object.prototype.hasOwnProperty.call(mergedDefinitions, rbdid)) {
      mergedDefinitions[rbdid] = definition
      definitionsChanged = true
    }
    return { rbdid, amount }
  })

  if (definitionsChanged) {
    // applyFieldMapper's `store` param is sometimes a { dispatch, getters }
    // wrapper (see products.js's detectAndMapProduct/detectAndMapProducts),
    // not a full Vuex store instance - no .commit available there. Route
    // through the root-level `adhocstore` action (store/index.js), which
    // wraps the same STORE_ADHOC_STORAGE mutation, via dispatch instead.
    // { root: true } is required when this dispatch came from inside a
    // namespaced module action, and is a harmless no-op otherwise.
    store.dispatch('adhocstore', { key: 'roleAdjustRuleDefinitions', data: mergedDefinitions }, { root: true })
  }

  product.productsettings.prices.role_adjust_prices = slimRules
  return product
}
